import { useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ContractTimeline } from "@/features/contracts/ContractTimeline";
import { useEventContracts } from "@/api/hooks/useEventContracts";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  QueryStateBoundary,
} from "@/components/ui";
import { getErrorMessage } from "@/api/client";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import { useContact } from "@/api/hooks/useContacts";
import {
  useEvent,
  useMarkBalancePaid,
  useMarkDepositPaid,
  usePatchEvent,
  useSetEventContract,
  useSetEventStatus,
} from "@/api/hooks/useEvents";
import {
  EVENT_STATUS_BADGE_VARIANT,
  EVENT_STATUS_LABELS,
  isTerminalEventStatus,
  nextEventStatus,
  type Event,
} from "@/api/types/event";
import { formatShortDate } from "./eventDate";
import { TaskListCard } from "./TaskListCard";
import { CaptureChecklistCard } from "./CaptureChecklistCard";
import { MenuShoppingListCard } from "./MenuShoppingListCard";
import { EventMoneySection } from "./EventMoneySection";
import { ProofTasksCard } from "./ProofTasksCard";
import { EventAssetsCard } from "./EventAssetsCard";
import { EventReferralsCard } from "./EventReferralsCard";

export function EventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: event, isLoading, error, refetch } = useEvent(id);
  const { data: contact } = useContact(event?.contact_id);

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6 sm:p-8">
      <Button variant="link" size="sm" onClick={() => navigate("/events")}>
        ← Back to events
      </Button>

      <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
        {event && (
          <>
            <EventHeaderCard event={event} contactName={contact?.name} />
            <MoneyCard event={event} />
            <EventMoneySection event={event} />
            <TaskListCard event={event} />
            <MenuShoppingListCard event={event} />
            <CaptureChecklistCard event={event} />
            <StatusCard event={event} />
            <ContractCard event={event} />
            <ProofTasksCard eventId={event.id} eventStatus={event.status} />
            <EventAssetsCard eventId={event.id} />
            <EventReferralsCard eventId={event.id} />
          </>
        )}
      </QueryStateBoundary>
    </div>
  );
}

// ---- Header --------------------------------------------------------------

function EventHeaderCard({ event, contactName }: { event: Event; contactName?: string }) {
  const patchEvent = usePatchEvent();
  const [venueName, setVenueName] = useState(event.venue_name ?? "");
  const [venueAddress, setVenueAddress] = useState(event.venue_address ?? "");
  const [guestCount, setGuestCount] = useState(String(event.guest_count));
  const [naGuestCount, setNaGuestCount] = useState(String(event.na_guest_count ?? ""));

  async function saveField(patch: Record<string, unknown>) {
    try {
      await patchEvent.mutateAsync({ id: event.id, ...patch });
    } catch (err) {
      toastError("Couldn't save", getErrorMessage(err));
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>{formatShortDate(event.event_date)} — {contactName ?? "Event"}</CardTitle>
          {event.event_time && <p className="text-sm text-text-secondary">{event.event_time}</p>}
        </div>
        <Badge variant={EVENT_STATUS_BADGE_VARIANT[event.status]} data-testid="event-status-badge">
          {EVENT_STATUS_LABELS[event.status]}
        </Badge>
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Venue name">
          <input
            value={venueName}
            onChange={(e) => setVenueName(e.target.value)}
            onBlur={() => venueName !== (event.venue_name ?? "") && void saveField({ venue_name: venueName || null })}
            className="h-9 w-full rounded-md border border-border bg-bg-card px-2 text-sm"
          />
        </Field>
        <Field label="Venue address">
          <input
            value={venueAddress}
            onChange={(e) => setVenueAddress(e.target.value)}
            onBlur={() =>
              venueAddress !== (event.venue_address ?? "") &&
              void saveField({ venue_address: venueAddress || null })
            }
            className="h-9 w-full rounded-md border border-border bg-bg-card px-2 text-sm"
          />
        </Field>
        <Field label="Guest count">
          <input
            type="number"
            min={0}
            value={guestCount}
            onChange={(e) => setGuestCount(e.target.value)}
            onBlur={() =>
              Number(guestCount) !== event.guest_count &&
              void saveField({ guest_count: Number(guestCount) || 0 })
            }
            className="h-9 w-full rounded-md border border-border bg-bg-card px-2 text-sm"
          />
        </Field>
        <Field label="NA guest count">
          <input
            type="number"
            min={0}
            value={naGuestCount}
            onChange={(e) => setNaGuestCount(e.target.value)}
            onBlur={() =>
              Number(naGuestCount || 0) !== (event.na_guest_count ?? 0) &&
              void saveField({ na_guest_count: naGuestCount === "" ? null : Number(naGuestCount) })
            }
            className="h-9 w-full rounded-md border border-border bg-bg-card px-2 text-sm"
          />
        </Field>
      </CardContent>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-medium text-text-secondary">{label}</span>
      {children}
    </label>
  );
}

// ---- Money ----------------------------------------------------------------

function MoneyCard({ event }: { event: Event }) {
  const markDeposit = useMarkDepositPaid();
  const markBalance = useMarkBalancePaid();

  async function handleDeposit() {
    try {
      await markDeposit.mutateAsync({ id: event.id });
      toastSuccess("Deposit marked paid");
    } catch (err) {
      toastError("Couldn't mark deposit paid", getErrorMessage(err));
    }
  }

  async function handleBalance() {
    try {
      await markBalance.mutateAsync({ id: event.id });
      toastSuccess("Balance marked paid");
    } catch (err) {
      toastError("Couldn't mark balance paid", getErrorMessage(err));
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Money</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm">
          <p data-testid="deposit-status">
            <span className="font-medium text-text-primary">Deposit: </span>
            {event.deposit_paid_at ? (
              <span className="text-success">Paid {formatShortDate(event.deposit_paid_at)}</span>
            ) : (
              <span className="text-text-muted">Not paid</span>
            )}
          </p>
          <p data-testid="balance-status">
            <span className="font-medium text-text-primary">Balance: </span>
            {event.balance_paid_at ? (
              <span className="text-success">Paid {formatShortDate(event.balance_paid_at)}</span>
            ) : (
              <span className="text-text-muted">
                Due {formatShortDate(event.balance_due_at)}
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          {!event.deposit_paid_at && (
            <Button size="sm" onClick={() => void handleDeposit()} disabled={markDeposit.isPending}>
              {markDeposit.isPending ? "Saving..." : "Mark deposit paid"}
            </Button>
          )}
          {!event.balance_paid_at && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => void handleBalance()}
              disabled={markBalance.isPending}
            >
              {markBalance.isPending ? "Saving..." : "Mark balance paid"}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ---- Status -----------------------------------------------------------

function StatusCard({ event }: { event: Event }) {
  const setStatus = useSetEventStatus();
  const [statusError, setStatusError] = useState<string | null>(null);
  const next = nextEventStatus(event.status);
  const canCancel = !isTerminalEventStatus(event.status);

  async function advance() {
    if (!next) return;
    setStatusError(null);
    try {
      await setStatus.mutateAsync({ id: event.id, status: next });
      toastSuccess(`Event marked ${EVENT_STATUS_LABELS[next]}`);
    } catch (err) {
      const message = getErrorMessage(err);
      setStatusError(message);
      toastError(`Couldn't mark as ${EVENT_STATUS_LABELS[next]}`, message);
    }
  }

  async function cancel() {
    setStatusError(null);
    try {
      await setStatus.mutateAsync({ id: event.id, status: "canceled" });
      toastSuccess("Event canceled");
    } catch (err) {
      const message = getErrorMessage(err);
      setStatusError(message);
      toastError("Couldn't cancel event", message);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Status</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {statusError && (
          <div
            className="rounded-md border border-danger/40 bg-danger/5 px-3 py-2 text-sm text-danger"
            data-testid="status-error"
            role="alert"
          >
            {statusError}
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          {next && (
            <Button onClick={() => void advance()} disabled={setStatus.isPending}>
              {setStatus.isPending ? "Saving..." : `Mark as ${EVENT_STATUS_LABELS[next]}`}
            </Button>
          )}
          {canCancel && (
            <Button variant="danger" onClick={() => void cancel()} disabled={setStatus.isPending}>
              Cancel event
            </Button>
          )}
          {!next && !canCancel && (
            <p className="text-sm text-text-muted">This event has reached its final status.</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ---- Contract / COI -----------------------------------------------------

function ContractCard({ event }: { event: Event }) {
  const patchEvent = usePatchEvent();
  const setContract = useSetEventContract();
  const [contractKey, setContractKey] = useState(event.contract_key ?? "");
  const { data: contracts } = useEventContracts(event.id);
  const contract = contracts?.[0];

  async function toggleCoi(checked: boolean) {
    try {
      await patchEvent.mutateAsync({ id: event.id, coi_sent: checked });
    } catch (err) {
      toastError("Couldn't update COI status", getErrorMessage(err));
    }
  }

  async function saveContract() {
    if (!contractKey.trim() || contractKey === event.contract_key) return;
    try {
      await setContract.mutateAsync({ id: event.id, contract_key: contractKey.trim() });
      toastSuccess("Contract reference saved");
    } catch (err) {
      toastError("Couldn't save contract reference", getErrorMessage(err));
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Contract & COI</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Checkbox
          label="COI sent to venue"
          checked={event.coi_sent}
          onChange={(e) => void toggleCoi(e.target.checked)}
        />
        <div>
          <p className="mb-1 text-xs font-medium text-text-secondary">Contract reference</p>
          <div className="flex gap-2">
            <input
              value={contractKey}
              onChange={(e) => setContractKey(e.target.value)}
              placeholder="e.g. contracts/2026-07-signature-pour.pdf"
              className="h-9 w-full rounded-md border border-border bg-bg-card px-2 text-sm"
              aria-label="Contract reference"
            />
            <Button
              size="sm"
              variant="secondary"
              onClick={() => void saveContract()}
              disabled={setContract.isPending || !contractKey.trim() || contractKey === event.contract_key}
            >
              {setContract.isPending ? "Saving..." : "Save"}
            </Button>
          </div>
          {event.contract_key && (
            <p className="mt-1 text-xs text-text-muted">Current: {event.contract_key}</p>
          )}
        </div>
        {contract && <ContractTimeline contractId={contract.id} />}
      </CardContent>
    </Card>
  );
}
